import React, { useCallback, useMemo, useState } from 'react';
import { BulkManageAction, type BulkManageRow } from '../types';
import {
  isRangeAnalysisBlockingHistoricalError,
  isBulkManageFundRow,
  rowHasHistoricalError,
  rowHasHistoricalWarning,
} from '../utils';
import { type AnalysisSubject, useHasFF } from 'venn-utils';
import { invalidate, logExhaustive, useMemoWithArgs } from 'venn-utils';
import type { ColDef, IRowNode } from 'ag-grid-community';
import { compact, some } from 'lodash';
import {
  dataSourceColDef,
  endDateColDef,
  frequencyColDef,
  getHoldingsProxyColDef,
  getReturnsProxyColDef,
  rangeColDef,
  startDateColDef,
} from '../columns';
import { autoGroupColDef } from '../columns/autoGroupColDef';
import { NameCellRenderer } from '../columns/NameCellRenderer';
import { NameHeaderPrimitive } from '../columns/NameHeaderPrimitive';

type Props = {
  subject: AnalysisSubject;
  onFundUpdated?: (fundId: string) => void;
  canEditProxies: boolean;
  setOpenedSecondaryId: (id: string) => void;
  setSelectedFundIds: (ids: string[]) => void;
  onReturnsProxyPickerOpen: () => void;
  onHoldingsProxyPickerOpen: () => void;
  rowData: BulkManageRow[];
  showLiquidatedInvestmentsToggle: boolean;
  showLiquidatedInvestments: boolean;
  setShowLiquidatedInvestments: (show: boolean) => void;
};
type PortfolioManagementColumns = {
  columnDefs: ColDef<BulkManageRow>[];
  autoGroupColumnDef: ColDef<BulkManageRow>;
};
export const usePortfolioManagementColumns = ({
  subject,
  onFundUpdated,
  setOpenedSecondaryId,
  canEditProxies,
  onReturnsProxyPickerOpen,
  onHoldingsProxyPickerOpen,
  rowData,
  showLiquidatedInvestmentsToggle,
  showLiquidatedInvestments,
  setShowLiquidatedInvestments,
}: Props): PortfolioManagementColumns => {
  const [rowWithOpenTooltip, setRowWithOpenTooltip] = useState<BulkManageRow['rowId'] | undefined>(undefined);
  const handleRowUpdate = useCallback(
    async (action: BulkManageAction, row: BulkManageRow) => {
      switch (action) {
        case BulkManageAction.FUND_MODIFIED:
          {
            const investmentId = isBulkManageFundRow(row) && row.investment.id;
            const subjectInvalidation = invalidate('investment', subject.id);
            const investmentInvalidation =
              investmentId && investmentId !== subject.id ? invalidate('investment', investmentId) : Promise.resolve();
            investmentId && onFundUpdated?.(investmentId);
            await Promise.allSettled([subjectInvalidation, investmentInvalidation]);
          }
          break;

        case BulkManageAction.INVESTMENT_FORECAST_MODIFIED:
          break;

        case BulkManageAction.FUND_HOLDINGS_PROXY_MODIFIED:
          {
            const investmentId = isBulkManageFundRow(row) && row.investment.id;
            const subjectInvalidation = invalidate('investmentsHoldingsProxy', [subject.id]);
            const investmentInvalidation =
              investmentId && investmentId !== subject.id
                ? invalidate('investmentsHoldingsProxy', [investmentId])
                : Promise.resolve();
            investmentId && onFundUpdated?.(investmentId);
            await Promise.allSettled([subjectInvalidation, investmentInvalidation]);
          }
          break;
        default:
          logExhaustive(action);
      }
    },
    [subject.id, onFundUpdated],
  );

  const selectProxyRow = useCallback(
    (proxyType: 'returns' | 'holdings') => (row: IRowNode<BulkManageRow>) => {
      const bulkManageRow = row.data;
      const fundId = bulkManageRow && isBulkManageFundRow(bulkManageRow) && bulkManageRow.investment.id;
      if (!fundId) {
        return;
      }

      setOpenedSecondaryId(fundId);

      if (proxyType === 'returns') {
        onReturnsProxyPickerOpen();
      } else {
        onHoldingsProxyPickerOpen();
      }
    },
    [onReturnsProxyPickerOpen, onHoldingsProxyPickerOpen, setOpenedSecondaryId],
  );

  const subjectHasHistoricalError = useMemo(
    () =>
      some(
        rowData,
        (data) => rowHasHistoricalError(data) && isRangeAnalysisBlockingHistoricalError(data.historicalError.value),
      ),
    [rowData],
  );

  const returnsProxyColDef = useMemoWithArgs(getReturnsProxyColDef, [
    canEditProxies,
    handleRowUpdate,
    selectProxyRow('returns'),
  ]);
  const holdingProxyColDef = useMemoWithArgs(getHoldingsProxyColDef, [
    canEditProxies,
    handleRowUpdate,
    selectProxyRow('holdings'),
  ]);
  const holdingProxyFF = useHasFF('holding_proxy_ff');
  const autoGroupColumnDef = useMemo(
    () =>
      autoGroupColDef({
        customHeaderComponent: {
          headerComponent: () => (
            <NameHeaderPrimitive.Root>
              {showLiquidatedInvestmentsToggle && (
                <NameHeaderPrimitive.Toggle
                  checked={showLiquidatedInvestments}
                  onCheckedChange={setShowLiquidatedInvestments}
                />
              )}
            </NameHeaderPrimitive.Root>
          ),
        },
        customInnerCellComponent: {
          innerRenderer: NameCellRenderer,
          innerRendererParams: {
            setRowWithOpenTooltip,
            shouldShowHistoricalError: true,
          },
        },
        showGroupHeader: false,
        showCheckbox: canEditProxies,
      }),
    [
      showLiquidatedInvestments,
      setShowLiquidatedInvestments,
      showLiquidatedInvestmentsToggle,
      setRowWithOpenTooltip,
      canEditProxies,
    ],
  );

  const shouldShowHypotheticalReturnExtension = useCallback(
    (rowData: BulkManageRow) => rowHasHistoricalWarning(rowData) && rowData.rowId === rowWithOpenTooltip,
    [rowWithOpenTooltip],
  );

  return {
    columnDefs: compact([
      rangeColDef({ subjectHasHistoricalError, shouldShowHypotheticalReturnExtension }),
      startDateColDef(!!subject?.portfolio?.historical, holdingProxyFF),
      endDateColDef(!!subject?.portfolio?.historical, holdingProxyFF),
      frequencyColDef,
      dataSourceColDef(holdingProxyFF),
      returnsProxyColDef,
      holdingProxyFF && holdingProxyColDef,
    ]),
    autoGroupColumnDef,
  };
};
